import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { cloudSprite, streakSprite } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * STORMLINE — a circuit strung through the eternal storm of a gas giant.
 * Layered cloud decks above and below, wind-driven spray past the canopy, and
 * lightning that spikes the whole sky and throws hard shadows for a heartbeat.
 */
export function buildStormline(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('stormline');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cy, cz, maxR } = ribbon;

  // ── sky: banded gas-giant murk with a lightning flicker uniform ──
  const skyMat = (() => {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: { uFlash: { value: 0 } },
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform float uFlash;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 zenith = vec3(0.030, 0.020, 0.070);
          vec3 depths = vec3(0.100, 0.065, 0.180);
          vec3 col = mix(depths, zenith, pow(h, 0.75));
          // horizontal storm bands sliding around the giant
          float bands = sin(vDir.y * 26.0 + sin(atan(vDir.x, vDir.z) * 3.0) * 1.5);
          col *= 1.0 + bands * 0.14;
          // lightning: cold white-violet bloom through the whole cloud layer
          col += vec3(0.70, 0.72, 1.0) * uFlash * (0.35 + 0.65 * (1.0 - h));
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
    return mat;
  })();

  // ── lights: violet storm-glow + the lightning strobe ──
  const boltLight = new THREE.DirectionalLight(0xe8ecff, 0);
  {
    const hemi = new THREE.HemisphereLight(0x574a8a, 0x140f26, 0.8);
    const key = new THREE.DirectionalLight(0x9a86d8, 0.75);
    key.position.set(cx + 400, cy + 800, cz - 300);
    boltLight.position.set(cx, cy + 900, cz);
    boltLight.target.position.set(cx, cy, cz);
    scene.add(hemi, key, boltLight, boltLight.target);
    objects.push(hemi, key, boltLight, boltLight.target);
  }

  // ── cloud decks: a dense floor of storm-tops below, ragged scud above ──
  {
    const tex = cloudSprite(17);
    disposables.push(tex);
    const decks: { y: number; count: number; color: number; sMin: number; sMax: number; opacity: number }[] = [
      { y: ribbon.minY - 240, count: quality === 'performance' ? 60 : 130, color: 0x4a3a78, sMin: 320, sMax: 720, opacity: 0.9 },
      { y: ribbon.minY - 150, count: quality === 'performance' ? 30 : 70, color: 0x6a4e94, sMin: 220, sMax: 480, opacity: 0.7 },
      { y: ribbon.maxY + 420, count: quality === 'performance' ? 24 : 55, color: 0x241a44, sMin: 380, sMax: 800, opacity: 0.8 },
    ];
    const geo = new THREE.PlaneGeometry(1, 1);
    disposables.push(geo);
    for (const deck of decks) {
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: deck.opacity,
        depthWrite: false,
        color: deck.color,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.InstancedMesh(geo, mat, deck.count);
      const dummy = new THREE.Object3D();
      for (let i = 0; i < deck.count; i++) {
        dummy.position.set(
          cx + rng.range(-2600, 2600),
          deck.y + rng.range(-40, 40),
          cz + rng.range(-2600, 2600)
        );
        dummy.rotation.set(-Math.PI / 2, 0, rng.range(0, Math.PI * 2));
        const s = rng.range(deck.sMin, deck.sMax);
        dummy.scale.set(s, s, s);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }
      scene.add(mesh);
      objects.push(mesh);
      disposables.push(mat);
    }
  }

  // ── storm platforms: shattered rock riding the updrafts (staggered drift) ──
  const rocks = (() => {
    const count = quality === 'performance' ? 12 : 24;
    const geo = new THREE.IcosahedronGeometry(1, 1);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x2a2244,
      emissive: 0x9a5cff,
      emissiveIntensity: 0.18,
      roughness: 0.85,
      metalness: 0.3,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    const speeds: number[] = [];
    const axes: THREE.Vector3[] = [];
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const ang = rng.range(0, Math.PI * 2);
      const dist = maxR + rng.range(200, 900);
      const x = cx + Math.cos(ang) * dist;
      const z = cz + Math.sin(ang) * dist;
      if (!clearOfRibbon(ribbon, x, z, 150)) continue;
      dummy.position.set(x, cy + rng.range(-160, 260), z);
      dummy.rotation.set(rng.range(0, 3), rng.range(0, 3), rng.range(0, 3));
      dummy.scale.setScalar(rng.range(14, 60));
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      speeds.push(rng.range(-0.08, 0.08));
      axes.push(new THREE.Vector3(rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1)).normalize());
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
    return { mesh, speeds, axes };
  })();

  // ── wind spray: near-horizontal streaks whipping past the camera ──
  const sprayCount = quality === 'performance' ? 220 : 520;
  const spray = (() => {
    const tex = streakSprite();
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(0.05, 2.6);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xb8a8e8,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, sprayCount);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    const offsets: THREE.Vector3[] = [];
    for (let i = 0; i < sprayCount; i++) {
      offsets.push(new THREE.Vector3(rng.range(-45, 45), rng.range(-24, 24), rng.range(-45, 45)));
    }
    scene.add(mesh);
    objects.push(mesh);
    return { mesh, offsets };
  })();
  const sprayDummy = new THREE.Object3D();

  // lightning schedule (deterministic per-build, chaotic in feel)
  const strikeRng = new Rng('stormline-bolts');
  let flash = 0;
  let nextStrike = strikeRng.range(1.5, 4);

  const rotQ = new THREE.Quaternion();
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  let frameCounter = 0;
  let t = 0;

  return {
    update(dt, camPos, _time) {
      t += dt;
      // lightning: brief spike, fast exponential decay, occasional double-strike
      if (t >= nextStrike) {
        flash = strikeRng.range(0.7, 1);
        boltLight.position.set(
          cx + strikeRng.range(-1, 1) * 1200,
          cy + 900,
          cz + strikeRng.range(-1, 1) * 1200
        );
        nextStrike = t + (strikeRng.chance(0.3) ? strikeRng.range(0.1, 0.25) : strikeRng.range(2.5, 8));
      }
      flash *= Math.exp(-dt * 9);
      skyMat.uniforms.uFlash.value = flash;
      boltLight.intensity = flash * 5.5;
      // storm platforms tumble — one third per frame
      if (dt > 0) {
        frameCounter++;
        const count = rocks.speeds.length;
        const third = Math.ceil(count / 3);
        const start = (frameCounter % 3) * third;
        const end = Math.min(count, start + third);
        for (let i = start; i < end; i++) {
          rocks.mesh.getMatrixAt(i, m);
          m.decompose(pos, q, scl);
          rotQ.setFromAxisAngle(rocks.axes[i], rocks.speeds[i] * dt * 3);
          q.multiply(rotQ);
          m.compose(pos, q, scl);
          rocks.mesh.setMatrixAt(i, m);
        }
        rocks.mesh.instanceMatrix.needsUpdate = true;
      }
      // spray races sideways, tilted hard by the wind
      for (let i = 0; i < spray.offsets.length; i++) {
        const o = spray.offsets[i];
        o.x += dt * 55;
        if (o.x > 45) o.x -= 90;
        sprayDummy.position.set(camPos.x + o.x, camPos.y + o.y, camPos.z + o.z);
        sprayDummy.rotation.set(0, ((i * 37) % 100) * 0.0628, Math.PI * 0.42);
        sprayDummy.updateMatrix();
        spray.mesh.setMatrixAt(i, sprayDummy.matrix);
      }
      spray.mesh.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
